import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/authStore";
import { notificationsApi } from "../api/notificationsApi";
import { pwaApi } from "../api/pwaApi";
import { useNotifications } from "../notifications/notificationsStore";
import { useToast } from "../ui/toast/ToastProvider";
import NotificationModal from "../components/ui/NotificationModal";
import AppSidebarV2 from "../components/layout/AppSidebarV2";
import {
  ensureNotificationPermission,
  getCurrentPushSubscription,
  subscribePush,
  unsubscribePush,
} from "../pwa/pushClient";
import { detectPwaCapabilities } from "../pwa/capabilities";
import { WEB_PUSH_PUBLIC_KEY } from "../config/env";
import { openExternalUrl } from "../utils/safeUrl";

const TAB_ALL = "all";
const TAB_UNREAD = "unread";
const TAB_SESSIONS = "sessions";
const TAB_ARCHIVED = "archived";

const CATEGORY_DEFS = [
  {
    id: "sessions",
    title: "Сессии",
    description: "Напоминания и подтверждения",
    icon: "📅",
    keys: ["sessionReminder"],
  },
  {
    id: "chat",
    title: "Чат",
    description: "Сообщения и реакции",
    icon: "💬",
    keys: ["chatMessage", "chatReaction"],
  },
  {
    id: "blog",
    title: "Статьи",
    description: "Посты, комментарии, реакции",
    icon: "📝",
    keys: ["blogNewPost", "blogCommentOnMyPublication", "blogReactionOnMyPublication"],
  },
  {
    id: "forum",
    title: "Форум",
    description: "Ответы и реакции",
    icon: "🗨️",
    keys: ["forumCommentOnMyPublication", "forumReactionOnMyPublication", "commentReply"],
  },
  {
    id: "stories",
    title: "Сторис",
    description: "Новые сторис и реакции",
    icon: "⭕",
    keys: ["storyNew", "storyReactionOnMyStory", "storyReplyOnMyStory"],
  },
  {
    id: "podcast",
    title: "Подкасты",
    description: "Новые эпизоды и реакции",
    icon: "🎙️",
    keys: ["podcastNew", "podcastEpisodeNew", "podcastCommentOnMyPublication", "podcastReactionOnMyPublication"],
  },
  {
    id: "library",
    title: "Литература",
    description: "Новые книги, комментарии, реакции",
    icon: "📚",
    keys: ["literatureNew", "literatureCommentOnMyPublication", "literatureReactionOnMyPublication"],
  },
  {
    id: "profile",
    title: "Профиль",
    description: "Подписки и избранное",
    icon: "👤",
    keys: ["profileSubscription", "favoriteAdded"],
  },
];

function asDate(iso) {
  try {
    return iso ? new Date(iso) : null;
  } catch {
    return null;
  }
}

function nowIso() {
  return new Date().toISOString();
}

function formatTime(iso) {
  const d = asDate(iso);
  if (!d) return "";
  return d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
}

function formatDateTime(iso) {
  const d = asDate(iso);
  if (!d) return "";
  return d.toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatRelativeDateTime(iso) {
  const d = asDate(iso);
  if (!d) return "без даты";
  const now = Date.now();
  const diffMs = now - d.getTime();
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (diffMs < hour) {
    const min = Math.max(1, Math.floor(diffMs / minute));
    return `${min} мин назад`;
  }
  if (diffMs < day) {
    const h = Math.max(1, Math.floor(diffMs / hour));
    return `${h} ч назад`;
  }
  return formatDateTime(iso);
}

function maskPushValue(value) {
  const v = String(value || "").trim();
  if (!v) return "";
  const tail = Math.min(10, v.length);
  return `***${v.slice(v.length - tail)}`;
}

function channelTitle(channel) {
  return String(channel || "").toLowerCase() === "fcm" ? "FCM" : "Web Push";
}

function formatDayTitle(iso) {
  const d = asDate(iso);
  if (!d) return "Без даты";
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diffDays = Math.round((startOfToday - startOfDay) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return "Сегодня";
  if (diffDays === 1) return "Вчера";
  return d.toLocaleDateString("ru-RU", { day: "numeric", month: "long", year: "numeric" });
}

function typeInfo(typeRaw) {
  const type = String(typeRaw || "").toUpperCase();
  if (type.includes("MESSAGE") || type.includes("CHAT")) {
    return { key: "chat", label: "Чат", icon: "💬" };
  }
  if (type.includes("BLOG")) {
    return { key: "blog", label: "Статья", icon: "📝" };
  }
  if (type.includes("FORUM") || type.includes("COMMENT_REPLY")) {
    return { key: "forum", label: "Форум", icon: "🗨️" };
  }
  if (type.includes("STORY")) {
    return { key: "stories", label: "Сторис", icon: "⭕" };
  }
  if (type.includes("PODCAST")) {
    return { key: "podcast", label: "Подкаст", icon: "🎙️" };
  }
  if (type.includes("LITERATURE")) {
    return { key: "library", label: "Литература", icon: "📚" };
  }
  if (
    type.includes("SESSION") ||
    type.includes("APPOINTMENT") ||
    type.includes("BOOKING") ||
    type.includes("PAYMENT") ||
    type.includes("REVIEW") ||
    type.includes("GROUP_SESSION")
  ) {
    return { key: "session", label: "Сессия", icon: "📅" };
  }
  if (type.includes("FAV") || type.includes("PROFILE")) {
    return { key: "profile", label: "Профиль", icon: "👤" };
  }
  return { key: "system", label: "Системное", icon: "🔔" };
}

function isSessionNotification(item) {
  return typeInfo(item?.type).key === "session";
}

function actorName(item) {
  const p = item?.payload || {};
  const list = [
    p.actorName,
    p.senderName,
    p.authorName,
    p.clientName,
    p.userName,
    p.psychologistName,
    p.withName,
    item?.createdBy,
  ];
  for (const v of list) {
    const s = String(v || "").trim();
    if (s) return s;
  }
  return "Бюро Души";
}

function actorInitials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "БД";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
}

function sessionBlock(item) {
  if (!isSessionNotification(item)) return null;
  const p = item?.payload || {};
  const startAt = p.startAt || p.sessionAt || p.startsAt || p.dateTime || p.when || null;
  const duration =
    Number(p.durationMinutes || p.duration || p.sessionDurationMinutes || 0) > 0
      ? Number(p.durationMinutes || p.duration || p.sessionDurationMinutes || 0)
      : null;
  const modeRaw = String(p.mode || p.sessionType || p.kind || "").toUpperCase();
  const mode = modeRaw.includes("GROUP") ? "Групповая" : "Индивидуальная";
  if (!startAt && !duration) return null;

  return {
    time: startAt ? formatTime(startAt) : "Скоро",
    label: `${mode}${duration ? ` · ${duration} мин` : ""}`,
  };
}

function groupByDay(items = []) {
  const map = new Map();
  for (const n of items) {
    const day = formatDayTitle(n.createdAt);
    if (!map.has(day)) map.set(day, []);
    map.get(day).push(n);
  }
  return Array.from(map.entries()).map(([title, list]) => ({ title, list }));
}

function detectBrowserName() {
  const ua = String(navigator?.userAgent || "");
  if (/YaBrowser/i.test(ua)) return "Yandex Browser";
  if (/Edg\//i.test(ua)) return "Edge";
  if (/Chrome\//i.test(ua)) return "Chrome";
  if (/Safari\//i.test(ua) && !/Chrome\//i.test(ua)) return "Safari";
  if (/Firefox\//i.test(ua)) return "Firefox";
  return "Браузер";
}

function detectPlatformName() {
  const ua = String(navigator?.userAgent || "");
  if (/iPhone|iPad|iPod/i.test(ua)) return "iOS";
  if (/Android/i.test(ua)) return "Android";
  if (/Mac OS X|Macintosh/i.test(ua)) return "macOS";
  if (/Windows/i.test(ua)) return "Windows";
  if (/Linux/i.test(ua)) return "Linux";
  return "Unknown";
}

function isCategoryEnabled(settings, keys = []) {
  if (!settings) return true;
  if (!keys.length) return true;
  return keys.every((k) => settings[k] !== false);
}

function applyCategoryToggle(source, keys = [], value) {
  const out = { ...(source || {}) };
  keys.forEach((k) => {
    out[k] = Boolean(value);
  });
  return out;
}

function diffSettingsPatch(prev, next) {
  const patch = {};
  const keys = new Set([
    ...Object.keys(prev || {}),
    ...Object.keys(next || {}),
  ]);
  keys.forEach((k) => {
    if (prev?.[k] !== next?.[k]) patch[k] = Boolean(next?.[k]);
  });
  return patch;
}

function skeletonRows() {
  return (
    <div className="notif-v2-skeleton" aria-hidden="true">
      <div className="notif-v2-skeleton__row" />
      <div className="notif-v2-skeleton__row" />
      <div className="notif-v2-skeleton__row" />
    </div>
  );
}

export default function Notifications() {
  const navigate = useNavigate();
  const toast = useToast();
  const { role, isAuthenticated, me, logout } = useAuth();
  const {
    unreadNotificationsCount,
    markRead,
    markAllRead,
    archive: archiveInStore,
    refreshPreview,
  } = useNotifications();

  const [tab, setTab] = React.useState(TAB_ALL);
  const [items, setItems] = React.useState([]);
  const [archivedItems, setArchivedItems] = React.useState([]);
  const [nextCursor, setNextCursor] = React.useState(null);
  const cursorRef = React.useRef(null);
  const loadSeqRef = React.useRef(0);
  const [serverUnreadCount, setServerUnreadCount] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [error, setError] = React.useState("");

  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [settings, setSettings] = React.useState(null);
  const [settingsDraft, setSettingsDraft] = React.useState(null);
  const [settingsLoading, setSettingsLoading] = React.useState(false);
  const [settingsSaving, setSettingsSaving] = React.useState(false);

  const [pushBusy, setPushBusy] = React.useState(false);
  const [pushMeta, setPushMeta] = React.useState({
    pushEnabled: false,
    fcmEnabled: false,
    webPushConfigured: false,
    vapidPublicKey: WEB_PUSH_PUBLIC_KEY || "",
    message: "",
  });
  const [pushStatus, setPushStatus] = React.useState({
    permission: "default",
    subscribed: false,
    endpoint: "",
    browser: "",
    platform: "",
    fallback: null,
  });
  const [pushDevices, setPushDevices] = React.useState([]);
  const [pushDevicesLoading, setPushDevicesLoading] = React.useState(false);
  const [pushDeviceBusyKey, setPushDeviceBusyKey] = React.useState("");

  const canOpenLiteratureEditor =
    role === "ADMIN" || role === "PSYCHOLOGIST" || role === "MODERATOR";

  const [selectedNotif, setSelectedNotif] = React.useState(null);
  const [notifModalOpen, setNotifModalOpen] = React.useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = React.useState(false);

  const loadNotifications = React.useCallback(
    async ({ reset = false } = {}) => {
      if (tab === TAB_ARCHIVED) {
        setLoading(false);
        return;
      }
      const seq = ++loadSeqRef.current;
      try {
        if (reset) {
          setLoading(true);
          setError("");
          cursorRef.current = null;
          setNextCursor(null);
        } else {
          setLoadingMore(true);
        }

        const res = await notificationsApi.list({
          onlyUnread: tab === TAB_UNREAD,
          limit: 20,
          cursor: reset ? null : cursorRef.current,
        });

        if (seq !== loadSeqRef.current) return;

        const list = Array.isArray(res?.items) ? res.items : [];
        setItems((prev) => {
          if (reset) return list;
          const base = prev || [];
          const seen = new Set(base.map((x) => x.id));
          const merged = [...base];
          list.forEach((n) => {
            if (!seen.has(n.id)) {
              merged.push(n);
              seen.add(n.id);
            }
          });
          return merged;
        });

        const next = res?.nextCursor || null;
        cursorRef.current = next;
        setNextCursor(next);
        if (Number.isFinite(Number(res?.unreadCount))) {
          setServerUnreadCount(Number(res.unreadCount));
        }
      } catch (e) {
        if (seq !== loadSeqRef.current) return;
        setError(e?.message || "Не удалось загрузить уведомления");
      } finally {
        if (seq === loadSeqRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [tab]
  );

  const loadNotificationSettings = React.useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      setSettingsLoading(true);
      const current = await notificationsApi.getSettings();
      setSettings(current);
      setSettingsDraft(current);
    } catch (e) {
      toast.error(e?.message || "Не удалось загрузить настройки уведомлений");
    } finally {
      setSettingsLoading(false);
    }
  }, [isAuthenticated, toast]);

  const refreshPushStatus = React.useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const sub = await getCurrentPushSubscription();
      const fallback = pwaApi.getLastFallbackSync?.() || null;
      setPushStatus({
        permission: String(typeof Notification !== "undefined" ? Notification.permission : "default"),
        subscribed: Boolean(sub?.endpoint),
        endpoint: String(sub?.endpoint || ""),
        browser: detectBrowserName(),
        platform: detectPlatformName(),
        fallback,
      });
    } catch {
      setPushStatus((prev) => ({
        ...prev,
        permission: String(typeof Notification !== "undefined" ? Notification.permission : "default"),
      }));
    }
  }, [isAuthenticated]);

  const loadPushDevices = React.useCallback(async () => {
    if (!isAuthenticated) {
      setPushDevices([]);
      return;
    }
    try {
      setPushDevicesLoading(true);
      const res = await pwaApi.listPushDevices();
      setPushDevices(Array.isArray(res?.items) ? res.items : []);
    } catch (e) {
      setPushDevices([]);
      toast.error(e?.message || "Не удалось загрузить подключенные устройства");
    } finally {
      setPushDevicesLoading(false);
    }
  }, [isAuthenticated, toast]);

  const loadPushMeta = React.useCallback(async () => {
    if (!isAuthenticated) {
      setPushMeta({
        pushEnabled: false,
        fcmEnabled: false,
        webPushConfigured: false,
        vapidPublicKey: WEB_PUSH_PUBLIC_KEY || "",
        message: "",
      });
      return;
    }
    try {
      const meta = await pwaApi.getPushMeta();
      setPushMeta({
        pushEnabled: Boolean(meta?.pushEnabled),
        fcmEnabled: Boolean(meta?.fcmEnabled),
        webPushConfigured: Boolean(meta?.webPushConfigured),
        vapidPublicKey: String(meta?.vapidPublicKey || WEB_PUSH_PUBLIC_KEY || "").trim(),
        message: String(meta?.message || "").trim(),
      });
    } catch {
      setPushMeta((prev) => ({
        ...prev,
        webPushConfigured: Boolean(prev.webPushConfigured),
        vapidPublicKey: String(WEB_PUSH_PUBLIC_KEY || "").trim(),
      }));
    }
  }, [isAuthenticated]);

  React.useEffect(() => {
    loadNotifications({ reset: true });
  }, [tab, loadNotifications]);

  React.useEffect(() => {
    loadNotificationSettings();
    loadPushMeta();
    refreshPushStatus();
    loadPushDevices();
  }, [loadNotificationSettings, loadPushMeta, refreshPushStatus, loadPushDevices]);

  const unreadCount = Number.isFinite(Number(serverUnreadCount))
    ? Number(serverUnreadCount)
    : Number(unreadNotificationsCount || 0);
  const currentEndpointMask = maskPushValue(pushStatus.endpoint);
  const hasAnyPushDevice = pushDevices.some((x) => x?.active);
  const pushIsActive = Boolean(pushStatus.subscribed);
  const pushStatusLabel = pushIsActive ? "Активно" : hasAnyPushDevice ? "На другом устройстве" : "Отключено";
  const pwaCaps = React.useMemo(() => detectPwaCapabilities(), [pushStatus.permission, pushStatus.subscribed]);
  const pushLocalWarning = !pwaCaps.canPush
    ? pwaCaps.pushUnavailableReason
    : !pushStatus.subscribed && hasAnyPushDevice
      ? "В базе есть подписка, но на этом устройстве Service Worker не активен. Нажмите «Включить на этом устройстве» для пересоздания подписки."
      : "";
  const userName = String(me?.name || me?.fullName || me?.email || "Пользователь").trim();
  const userRoleLabel = role === "ADMIN" ? "Администратор" : role === "PSYCHOLOGIST" ? "Психолог" : "Пользователь";

  const filteredItems = React.useMemo(() => {
    if (tab === TAB_ARCHIVED) return archivedItems;
    let out = [...(items || [])];
    if (tab === TAB_SESSIONS) out = out.filter((x) => isSessionNotification(x));
    if (tab === TAB_UNREAD) out = out.filter((x) => !x.readAt);
    return out;
  }, [tab, items, archivedItems]);

  const groups = React.useMemo(() => groupByDay(filteredItems), [filteredItems]);

  const openNotification = async (item) => {
    if (!item) return;

    if (!item.readAt) {
      setItems((prev) => (prev || []).map((x) => (x.id === item.id ? { ...x, readAt: nowIso() } : x)));
      try {
        await markRead(item.id);
      } catch {}
    }

    setSelectedNotif(item);
    setNotifModalOpen(true);
  };

  const onMarkAllRead = async () => {
    try {
      await markAllRead();
      setItems((prev) => (prev || []).map((x) => ({ ...x, readAt: x.readAt || nowIso() })));
      setServerUnreadCount(0);
      toast.success("Все уведомления помечены как прочитанные");
    } catch (e) {
      toast.error(e?.message || "Не удалось отметить все уведомления");
    }
  };

  const onArchive = async (item) => {
    if (!item?.id) return;
    setItems((prev) => (prev || []).filter((x) => x.id !== item.id));
    setArchivedItems((prev) => [{ ...item, archivedAt: nowIso() }, ...(prev || [])]);
    try {
      await archiveInStore(item.id);
      refreshPreview?.();
    } catch (e) {
      toast.error(e?.message || "Не удалось отправить в архив");
      loadNotifications({ reset: true });
    }
  };

  const onToggleCategoryInstant = async (categoryId, enabled) => {
    const category = CATEGORY_DEFS.find((x) => x.id === categoryId);
    if (!category || !settings) return;
    const next = applyCategoryToggle(settings, category.keys, enabled);
    const patch = diffSettingsPatch(settings, next);
    if (!Object.keys(patch).length) return;
    try {
      setSettingsSaving(true);
      const saved = await notificationsApi.updateSettings(patch);
      setSettings(saved);
      setSettingsDraft(saved);
      toast.success("Настройки сохранены");
    } catch (e) {
      toast.error(e?.message || "Не удалось сохранить настройки");
    } finally {
      setSettingsSaving(false);
    }
  };

  const onSaveSettingsModal = async () => {
    if (!settings || !settingsDraft) {
      setSettingsOpen(false);
      return;
    }
    const patch = diffSettingsPatch(settings, settingsDraft);
    if (!Object.keys(patch).length) {
      setSettingsOpen(false);
      return;
    }
    try {
      setSettingsSaving(true);
      const saved = await notificationsApi.updateSettings(patch);
      setSettings(saved);
      setSettingsDraft(saved);
      setSettingsOpen(false);
      toast.success("Настройки уведомлений обновлены");
    } catch (e) {
      toast.error(e?.message || "Не удалось сохранить настройки");
    } finally {
      setSettingsSaving(false);
    }
  };

  const onTogglePush = async () => {
    try {
      setPushBusy(true);
      if (pushStatus.subscribed && pushStatus.endpoint) {
        await pwaApi.unsubscribePush(pushStatus.endpoint);
        await unsubscribePush();
        toast.success("Push-уведомления отключены");
      } else {
        const caps = detectPwaCapabilities();
        if (!caps.canPush) {
          toast.error(caps.pushUnavailableReason || "В этом браузере Push API не поддерживается");
          await refreshPushStatus();
          return;
        }
        if (!pushMeta.pushEnabled || pushMeta.webPushConfigured === false) {
          toast.error(pushMeta.message || "Push временно отключены или не настроены на сервере");
          return;
        }
        const vapidPublicKey = String(pushMeta.vapidPublicKey || WEB_PUSH_PUBLIC_KEY || "").trim();
        if (!vapidPublicKey) {
          toast.error("Push временно недоступны: на сервере не настроен публичный VAPID-ключ");
          return;
        }

        const permission = await ensureNotificationPermission();
        if (permission !== "granted") {
          toast.error("Разрешите уведомления в браузере");
          await refreshPushStatus();
          return;
        }
        const sub = await subscribePush(vapidPublicKey);
        await pwaApi.subscribePush(sub, {
          browser: detectBrowserName(),
          platform: detectPlatformName(),
          standalone: Boolean(window.matchMedia?.("(display-mode: standalone)")?.matches),
        });
        toast.success("Push-уведомления включены");
      }
    } catch (e) {
      toast.error(e?.message || "Не удалось изменить статус Push");
    } finally {
      await refreshPushStatus();
      await loadPushDevices();
      setPushBusy(false);
    }
  };

  const onDisconnectPushDevice = async (device) => {
    if (!device?.id || !device?.channel) return;
    const key = `${device.channel}:${device.id}`;
    try {
      setPushDeviceBusyKey(key);
      const isCurrentWebPush =
        device.channel === "webpush" &&
        Boolean(pushStatus.endpoint) &&
        Boolean(currentEndpointMask) &&
        currentEndpointMask === String(device.valueMasked || "");

      if (isCurrentWebPush) {
        await pwaApi.unsubscribePush(pushStatus.endpoint);
        await unsubscribePush();
      }

      await pwaApi.disconnectPushDevice(device.channel, device.id);
      toast.success("Устройство отключено");
    } catch (e) {
      toast.error(e?.message || "Не удалось отключить устройство");
    } finally {
      await refreshPushStatus();
      await loadPushDevices();
      setPushDeviceBusyKey("");
    }
  };

  return (
    <div className="podcasts-hub notif-v2-layout notif-v2-shell">
      <div className="podcasts-shell">
        <AppSidebarV2
          activeKey="notifications"
          mobileOpen={mobileSidebarOpen}
          onCloseMobile={() => setMobileSidebarOpen(false)}
          userName={userName}
          roleLabel={userRoleLabel}
          canOpenLiteratureEditor={canOpenLiteratureEditor}
          onLogout={async () => {
            await logout?.();
            navigate("/login", { replace: true });
          }}
        />

        <button
          className={`podcasts-overlay ${mobileSidebarOpen ? "is-open" : ""}`}
          type="button"
          aria-label="Закрыть меню"
          onClick={() => setMobileSidebarOpen(false)}
        />

        <div className="podcasts-main notif-v2-shell__main">
          <div className="notif-v2">
          <div className="notif-v2__topbar">
            <div className="notif-v2__titleWrap">
              <button
                type="button"
                className="podcasts-topbar__menu-btn"
                onClick={() => setMobileSidebarOpen(true)}
                aria-label="Открыть меню"
              >
                ☰
              </button>
              <h1 className="notif-v2__title">Уведомления</h1>
              <div className="notif-v2__chip">
                <span className="notif-v2__chipDot" />
                {unreadCount > 0 ? `${unreadCount} новых` : "Новых нет"}
              </div>
            </div>

            <div className="notif-v2__tabsWrap">
              <div className="notif-v2__tabs">
                <button type="button" className={tab === TAB_ALL ? "active" : ""} onClick={() => setTab(TAB_ALL)}>
                  Все
                </button>
                <button type="button" className={tab === TAB_UNREAD ? "active" : ""} onClick={() => setTab(TAB_UNREAD)}>
                  Непрочитанные
                </button>
                <button type="button" className={tab === TAB_SESSIONS ? "active" : ""} onClick={() => setTab(TAB_SESSIONS)}>
                  Сессии
                </button>
                <button type="button" className={tab === TAB_ARCHIVED ? "active" : ""} onClick={() => setTab(TAB_ARCHIVED)}>
                  Архив
                </button>
              </div>
            </div>

            <div className="notif-v2__actions">
              <button type="button" className="notif-v2-btn" onClick={onMarkAllRead} disabled={!items.length}>
                Прочитать все
              </button>
              <button type="button" className="notif-v2-btn notif-v2-btn--icon" onClick={() => setSettingsOpen(true)}>
                ⚙
              </button>
            </div>
          </div>

          <div className="notif-v2__content">
            <div className="notif-v2-feed">
              {loading ? (
                skeletonRows()
              ) : error ? (
                <div className="notif-v2-empty">{error}</div>
              ) : filteredItems.length === 0 ? (
                <div className="notif-v2-empty">
                  <div className="notif-v2-empty__icon">🔔</div>
                  <div className="notif-v2-empty__title">Пока пусто</div>
                  <div className="notif-v2-empty__sub">В этом разделе уведомлений пока нет.</div>
                </div>
              ) : (
                <>
                  {groups.map((g) => (
                    <div key={g.title} className="notif-v2-group">
                      <div className="notif-v2-group__label">{g.title}</div>
                      {g.list.map((item) => {
                        const type = typeInfo(item.type);
                        const actor = actorName(item);
                        const session = sessionBlock(item);
                        const preview = item.message || "";
                        return (
                          <div
                            key={`${item.id}-${item.createdAt || ""}`}
                            className={`notif-v2-card ${item.readAt ? "" : "unread"}`.trim()}
                            data-type={type.key}
                          >
                            <button type="button" className="notif-v2-card__main" onClick={() => openNotification(item)}>
                              <div className={`notif-v2-card__avatar is-${type.key}`}>
                                {actorInitials(actor)}
                                <span className="notif-v2-card__avatarTag" aria-hidden="true">
                                  {type.icon}
                                </span>
                              </div>
                              <div className="notif-v2-card__body">
                                <div className="notif-v2-card__meta">
                                  <span className="notif-v2-card__actor">{actor}</span>
                                  <span className={`notif-v2-card__pill is-${type.key}`}>{type.label}</span>
                                </div>
                                <div className="notif-v2-card__title">{item.title}</div>
                                {preview ? <div className="notif-v2-card__preview">{preview}</div> : null}
                                {session ? (
                                  <div className="notif-v2-card__session">
                                    <div className="notif-v2-card__sessionTime">{session.time}</div>
                                    <div className="notif-v2-card__sessionLabel">{session.label}</div>
                                  </div>
                                ) : null}
                              </div>
                              <div className="notif-v2-card__right">
                                <div className="notif-v2-card__time">{formatTime(item.createdAt)}</div>
                                {!item.readAt ? <span className="notif-v2-card__dot" /> : null}
                              </div>
                            </button>
                            <div className="notif-v2-card__controls">
                              {!item.readAt ? (
                                <button
                                  type="button"
                                  className="notif-v2-icon-btn"
                                  title="Отметить прочитанным"
                                  onClick={() => openNotification(item)}
                                >
                                  ✓
                                </button>
                              ) : null}
                              <button
                                type="button"
                                className="notif-v2-icon-btn"
                                title="В архив"
                                onClick={() => onArchive(item)}
                              >
                                🗂
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))}

                  {tab !== TAB_ARCHIVED && nextCursor ? (
                    <button
                      type="button"
                      className="notif-v2-load-more"
                      onClick={() => loadNotifications({ reset: false })}
                      disabled={loadingMore}
                    >
                      {loadingMore ? "Загружаем..." : "Загрузить ещё"}
                    </button>
                  ) : null}
                </>
              )}
            </div>

            <div className="notif-v2-side">
              <section className="notif-v2-panel">
                <div className="notif-v2-panel__head">
                  <div className="notif-v2-panel__title">Push-уведомления</div>
                  <span className={`notif-v2-status ${pushIsActive ? "is-on" : ""}`}>
                    {pushStatusLabel}
                  </span>
                </div>
                <div className="notif-v2-panel__body">
                  {pushLocalWarning ? <div className="notif-v2-subtle">{pushLocalWarning}</div> : null}
                  {pushDevicesLoading ? (
                    <div className="notif-v2-subtle">Загрузка устройств...</div>
                  ) : pushDevices.length ? (
                    <div className="notif-v2-push-list">
                      {pushDevices.map((device, idx) => {
                        const isCurrentWebPush =
                          device.channel === "webpush" &&
                          Boolean(currentEndpointMask) &&
                          currentEndpointMask === String(device.valueMasked || "");
                        const deviceKey = String(device.key || `${device.channel}:${device.id || idx}`);
                        const rowBusy = pushDeviceBusyKey === deviceKey;
                        const titleParts = [device.browser, device.platform].filter(Boolean);
                        const title = titleParts.length ? titleParts.join(" · ") : channelTitle(device.channel);
                        const updatedLabel = formatRelativeDateTime(device.updatedAt);
                        return (
                          <div key={deviceKey} className="notif-v2-push-device">
                            <div className="notif-v2-push-device__icon">{device.channel === "fcm" ? "📱" : "💻"}</div>
                            <div className="notif-v2-push-device__info">
                              <div className="notif-v2-push-device__name">{title}</div>
                              <div className="notif-v2-push-device__meta">
                                {channelTitle(device.channel)}
                                {device.provider ? ` · ${device.provider}` : ""}
                                {` · ${updatedLabel}`}
                                {isCurrentWebPush ? " · Это устройство" : ""}
                              </div>
                              {isCurrentWebPush ? (
                                <div className="notif-v2-push-device__meta">Разрешение: {pushStatus.permission}</div>
                              ) : null}
                            </div>
                            <div className="notif-v2-push-device__right">
                              <span className={`notif-v2-push-device__dot ${device.active ? "is-on" : ""}`} />
                              <button
                                type="button"
                                className="notif-v2-btn notif-v2-btn--sm"
                                disabled={rowBusy || pushBusy}
                                onClick={() => onDisconnectPushDevice(device)}
                              >
                                {rowBusy ? "..." : "Отключить"}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="notif-v2-subtle">Нет подключенных устройств</div>
                  )}

                  <div className="notif-v2-push-actions">
                    <button type="button" className="notif-v2-btn" onClick={onTogglePush} disabled={pushBusy}>
                      {pushBusy ? "Подождите..." : pushStatus.subscribed ? "Отключить на этом устройстве" : "Включить на этом устройстве"}
                    </button>
                  </div>
                </div>
              </section>

              <section className="notif-v2-panel">
                <div className="notif-v2-panel__head">
                  <div className="notif-v2-panel__title">Быстрые настройки</div>
                </div>
                <div className="notif-v2-panel__body">
                  {settingsLoading ? (
                    <div className="notif-v2-subtle">Загрузка настроек...</div>
                  ) : settings ? (
                    CATEGORY_DEFS.map((cat) => (
                      <div key={cat.id} className="notif-v2-setting-row">
                        <div className="notif-v2-setting-row__left">
                          <div className="notif-v2-setting-row__icon">{cat.icon}</div>
                          <div className="notif-v2-setting-row__texts">
                            <div className="notif-v2-setting-row__title">{cat.title}</div>
                            <div className="notif-v2-setting-row__sub">{cat.description}</div>
                          </div>
                        </div>
                        <label className="notif-v2-switch">
                          <input
                            type="checkbox"
                            checked={isCategoryEnabled(settings, cat.keys)}
                            disabled={settingsSaving}
                            onChange={(e) => onToggleCategoryInstant(cat.id, e.target.checked)}
                          />
                          <span className="notif-v2-switch__track" />
                          <span className="notif-v2-switch__thumb" />
                        </label>
                      </div>
                    ))
                  ) : (
                    <div className="notif-v2-subtle">Настройки недоступны</div>
                  )}
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
      </div>

      <NotificationModal
        open={notifModalOpen}
        notification={selectedNotif}
        onClose={() => {
          setNotifModalOpen(false);
          setSelectedNotif(null);
        }}
        onGo={(href) => {
          if (!href) return;
          setNotifModalOpen(false);
          setSelectedNotif(null);
          if (/^https?:\/\//i.test(String(href))) {
            openExternalUrl(href);
          } else {
            navigate(href);
          }
        }}
      />

      <div className={`notif-v2-modal ${settingsOpen ? "is-open" : ""}`} onClick={() => setSettingsOpen(false)}>
        <div className="notif-v2-modal__card" onClick={(e) => e.stopPropagation()}>
          <div className="notif-v2-modal__head">
            <h2>Настройки уведомлений</h2>
            <button type="button" className="notif-v2-btn notif-v2-btn--icon" onClick={() => setSettingsOpen(false)}>
              ✕
            </button>
          </div>
          <div className="notif-v2-modal__body">
            {settingsDraft ? (
              CATEGORY_DEFS.map((cat) => (
                <div key={cat.id} className="notif-v2-setting-row">
                  <div className="notif-v2-setting-row__left">
                    <div className="notif-v2-setting-row__icon">{cat.icon}</div>
                    <div className="notif-v2-setting-row__texts">
                      <div className="notif-v2-setting-row__title">{cat.title}</div>
                      <div className="notif-v2-setting-row__sub">{cat.description}</div>
                    </div>
                  </div>
                  <label className="notif-v2-switch">
                    <input
                      type="checkbox"
                      checked={isCategoryEnabled(settingsDraft, cat.keys)}
                      onChange={(e) => {
                        setSettingsDraft((prev) => applyCategoryToggle(prev, cat.keys, e.target.checked));
                      }}
                    />
                    <span className="notif-v2-switch__track" />
                    <span className="notif-v2-switch__thumb" />
                  </label>
                </div>
              ))
            ) : (
              <div className="notif-v2-subtle">Нет данных для редактирования</div>
            )}
          </div>
          <div className="notif-v2-modal__foot">
            <button type="button" className="notif-v2-btn" onClick={() => setSettingsOpen(false)}>
              Отмена
            </button>
            <button
              type="button"
              className="notif-v2-btn notif-v2-btn--dark"
              disabled={settingsSaving}
              onClick={onSaveSettingsModal}
            >
              {settingsSaving ? "Сохранение..." : "Сохранить"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
